import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageSquare } from "lucide-react";
import { Card, field, btn, fmtTs, fmtTime } from "./ui";
import { fetchContent } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatTab = ({ auth }) => {
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [settings, setSettings] = useState({ enabled: true, greeting: "" });
  const [saving, setSaving] = useState(false);
  const bottomRef = useRef(null);

  const loadChats = () =>
    axios.get(`${API}/admin/chats`, auth).then((r) => setChats(r.data)).catch(() => {});

  const loadThread = (id) =>
    axios.get(`${API}/chat/${id}`).then((r) => setThread(r.data)).catch(() => setThread(null));

  useEffect(() => {
    loadChats();
    fetchContent(true).then((c) => {
      setSettings({
        enabled: c?.chat?.enabled !== false,
        greeting: c?.chat?.greeting || "Hey! Thanks for reaching out — leave your question here and I'll get back to you within a few hours.",
      });
    });
    const t = setInterval(loadChats, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!active) return;
    loadThread(active);
    axios.post(`${API}/admin/chats/${active}/read`, {}, auth).then(loadChats).catch(() => {});
    const t = setInterval(() => loadThread(active), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const c = await fetchContent(true);
      await axios.put(`${API}/admin/content`, { ...c, chat: settings }, auth);
      toast.success(settings.enabled ? "Chat is ON." : "Chat is OFF — vacation mode.");
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    try {
      await axios.post(`${API}/admin/chats/${active}/reply`, { text: reply.trim() }, auth);
      setReply("");
      loadThread(active);
    } catch { toast.error("Reply failed."); }
  };

  const toggleStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/chats/${id}/status`, { status }, auth);
      loadChats();
      if (active === id) loadThread(id);
    } catch { toast.error("Update failed."); }
  };

  return (
    <div className="space-y-8" data-testid="chat-tab">
      <Card title="Chat Settings" color="bg-staxyellow" testid="chat-settings-card"
        action={
          <button data-testid="chat-settings-save" onClick={saveSettings} disabled={saving} className={`${btn} bg-ink text-white`}>
            {saving ? "Saving..." : "Save"}
          </button>
        }>
        <div className="flex items-center gap-3">
          <span className="font-mono2 text-xs font-bold uppercase tracking-widest text-ink/60">Website chat</span>
          <button
            role="switch"
            aria-checked={settings.enabled}
            data-testid="chat-enabled-switch"
            onClick={() => setSettings((s) => ({ ...s, enabled: !s.enabled }))}
            className={`relative h-6 w-12 border-2 border-ink transition-colors ${settings.enabled ? "bg-staxyellow" : "bg-white"}`}
          >
            <span className={`absolute top-0.5 h-4 w-4 bg-ink transition-all ${settings.enabled ? "left-[26px]" : "left-0.5"}`} />
          </button>
          <span className={`font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] ${settings.enabled ? "text-ink" : "text-ink/40"}`}>
            {settings.enabled ? "On" : "Off (vacation)"}
          </span>
        </div>
        <p className="mt-2 font-mono2 text-[10px] uppercase tracking-widest text-ink/45">
          Off = the chat bubble disappears from your site.
        </p>
        <textarea
          data-testid="chat-greeting"
          value={settings.greeting}
          onChange={(e) => setSettings((s) => ({ ...s, greeting: e.target.value }))}
          placeholder="First message visitors see..."
          className={`${field} mt-3 min-h-[70px] resize-none`}
        />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Conversations" color="bg-staxcyan" testid="chat-list">
          {chats.length === 0 && <p className="font-mono2 text-xs uppercase tracking-widest text-ink/40">No chats yet.</p>}
          <div className="space-y-2">
            {chats.map((c) => (
              <button
                key={c.id}
                data-testid={`chat-item-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`w-full border-2 p-3 text-left transition-colors ${active === c.id ? "border-ink bg-staxyellow" : "border-ink/15 bg-white hover:border-ink"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display uppercase text-sm">{c.name}</span>
                  {c.unread_admin > 0 && (
                    <span className="border-2 border-ink bg-staxmagenta px-1.5 font-mono2 text-[10px] font-bold text-white">
                      {c.unread_admin}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-xs text-ink/55">
                  {c.preview_sender === "visitor" ? "→ " : ""}{c.preview || "No messages"}
                </p>
                <p className="mt-0.5 font-mono2 text-[9px] uppercase tracking-widest text-ink/40">
                  {c.status} · {fmtTs(c.last_message_at)}
                </p>
              </button>
            ))}
          </div>
        </Card>

        <div className="lg:col-span-2">
          <Card
            title={thread ? thread.chat.name : "Pick a conversation"}
            color="bg-staxmagenta"
            testid="chat-thread-card"
            action={thread && (
              thread.chat.status === "open" ? (
                <button data-testid={`chat-close-${thread.chat.id}`} onClick={() => toggleStatus(thread.chat.id, "closed")} className={`${btn} bg-white text-ink`}>
                  Close Chat
                </button>
              ) : (
                <button data-testid={`chat-reopen-${thread.chat.id}`} onClick={() => toggleStatus(thread.chat.id, "open")} className={`${btn} bg-staxcyan text-ink`}>
                  Reopen
                </button>
              )
            )}
          >
            {!thread && (
              <p className="flex items-center gap-2 font-mono2 text-xs uppercase tracking-widest text-ink/40">
                <MessageSquare size={14} /> Select a chat on the left.
              </p>
            )}
            {thread && (
              <>
                <div data-testid="chat-thread" data-lenis-prevent className="max-h-[380px] space-y-2 overflow-y-auto pr-1">
                  {thread.messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] border-2 border-ink px-3 py-2 text-sm ${
                        m.sender === "admin" ? "bg-staxcyan text-ink" : "bg-white text-ink"
                      }`}>
                        <p>{m.text}</p>
                        <p className="mt-0.5 font-mono2 text-[9px] uppercase tracking-widest opacity-50">
                          {m.sender === "admin" ? "You" : thread.chat.name} · {fmtTime(m.ts)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                <div className="mt-4 flex gap-2">
                  <input
                    data-testid="chat-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendReply()}
                    placeholder="Type your reply..."
                    className={field}
                  />
                  <button data-testid="chat-reply-send" onClick={sendReply} className={`${btn} bg-staxmagenta text-white shrink-0`}>
                    <Send size={15} />
                  </button>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};
